import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Assignment,
  Business,
  HealthAndSafety,
  ArrowForward,
  Schedule,
  TrendingUp,
  CheckCircle,
  Pending,
  Today,
} from '@mui/icons-material';
import { requestService } from '../../services/requestService';
import { serviceProviderService } from '../../services/serviceProviderService';

// Memoized RecentRequestItem component
const RecentRequestItem = React.memo(({ request, index, total, onNavigate }: {
  request: any;
  index: number;
  total: number;
  onNavigate: (path: string) => void;
}) => {
  const requestTitle = useMemo(() => {
    // Handle both Strapi v4 format (attributes) and direct format
    const title = request.attributes?.title || request.title;
    const description = request.attributes?.description || request.description;
    const id = request.id;
    return title || description?.substring(0, 40) || `Request #${id}`;
  }, [request.title, request.description, request.id, request.attributes?.title, request.attributes?.description]);

  const formattedDate = useMemo(() => {
    // Handle both Strapi v4 format (attributes.createdAt) and direct format
    const createdAt = request.attributes?.createdAt || request.createdAt;
    if (!createdAt) return 'N/A';
    const date = new Date(createdAt);
    if (isNaN(date.getTime())) return 'N/A';
    // Show both date and time for better clarity
    return date.toLocaleString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, [request.createdAt, request.attributes?.createdAt]);

  const statusColor = useMemo(() => {
    // Handle both Strapi v4 format (attributes) and direct format
    const status = request.attributes?.status || request.status;
    return status === 'completed' ? 'success' : status === 'active' ? 'info' : 'warning';
  }, [request.status, request.attributes?.status]);

  return (
    <React.Fragment>
      <ListItem sx={{ px: 0, cursor: 'pointer' }} onClick={() => onNavigate('/requests')}>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">{requestTitle}</Typography>
              <Chip label={(request.attributes?.status || request.status) || 'pending'} size="small" color={statusColor} />
            </Box>
          }
          secondary={formattedDate}
        />
      </ListItem>
      {index < total - 1 && <Divider />}
    </React.Fragment>
  );
});

RecentRequestItem.displayName = 'RecentRequestItem';

// Memoized StatCard component to prevent unnecessary re-renders
const StatCard = React.memo(({ title, value, icon, color, onClick }: any) => (
  <Card elevation={2} sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 6 } : {} }} onClick={onClick}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>{icon}</Avatar>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>{value}</Typography>
      <Typography color="text.secondary" variant="body2">{title}</Typography>
    </CardContent>
  </Card>
));

StatCard.displayName = 'StatCard';

const OperationsOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRequests: 0,
    todayRequests: 0,
    pendingRequests: 0,
    activeRequests: 0,
    completedRequests: 0,
    totalServiceProviders: 0,
    activeServiceProviders: 0,
    recentRequests: [] as any[],
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Optimize count queries by using minimal pageSize to reduce payload size
      // We only need the pagination metadata, not the actual data
      const countParams = { 'pagination[pageSize]': 1 };
      
      // Get today's date at midnight for filtering
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();
      
      const [requestsTotal, requestsToday, requestsPending, requestsActive, requestsCompleted, serviceProvidersTotal, serviceProvidersActive, requestsRecent] = await Promise.all([
        requestService.getAll(countParams).catch(() => ({ meta: { pagination: { total: 0 } } })),
        requestService.getAll({ ...countParams, 'filters[createdAt][$gte]': todayISO }).catch(() => ({ meta: { pagination: { total: 0 } } })),
        requestService.getAll({ ...countParams, 'filters[status][$eq]': 'pending' }).catch(() => ({ meta: { pagination: { total: 0 } } })),
        requestService.getAll({ ...countParams, 'filters[status][$eq]': 'active' }).catch(() => ({ meta: { pagination: { total: 0 } } })),
        requestService.getAll({ ...countParams, 'filters[status][$eq]': 'completed' }).catch(() => ({ meta: { pagination: { total: 0 } } })),
        serviceProviderService.getAll(countParams).catch(() => ({ meta: { pagination: { total: 0 } } })),
        serviceProviderService.getAll({ ...countParams, 'filters[isActive][$eq]': true }).catch(() => ({ meta: { pagination: { total: 0 } } })),
        // For recent requests, fetch with createdAt explicitly included
        requestService.getAll({ 
          'pagination[pageSize]': 5, 
          'sort[0]': 'createdAt:desc'
        }).catch(() => ({ data: [] })),
      ]);

      setStats({
        totalRequests: requestsTotal.meta?.pagination?.total || 0,
        todayRequests: requestsToday.meta?.pagination?.total || 0,
        pendingRequests: requestsPending.meta?.pagination?.total || 0,
        activeRequests: requestsActive.meta?.pagination?.total || 0,
        completedRequests: requestsCompleted.meta?.pagination?.total || 0,
        totalServiceProviders: serviceProvidersTotal.meta?.pagination?.total || 0,
        activeServiceProviders: serviceProvidersActive.meta?.pagination?.total || 0,
        recentRequests: requestsRecent.data || [],
      });
    } catch (error) {
      console.error('Error fetching operations data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Operations Overview</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>{t('operations.overview')}</Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 3, mb: 4 }}>
          <StatCard title={t('operations.totalRequests')} value={stats.totalRequests} icon={<Assignment />} color="primary.main" onClick={() => navigate('/requests')} />
          <StatCard 
            title={t('operations.todayRequests')} 
            value={stats.todayRequests} 
            icon={<Today />} 
            color="secondary.main" 
            onClick={() => navigate('/requests?tab=today')} 
          />
          <StatCard title={t('operations.pendingRequests')} value={stats.pendingRequests} icon={<Pending />} color="warning.main" onClick={() => navigate('/requests')} />
          <StatCard title={t('operations.activeRequests')} value={stats.activeRequests} icon={<TrendingUp />} color="info.main" onClick={() => navigate('/requests')} />
          <StatCard title={t('operations.completedRequests')} value={stats.completedRequests} icon={<CheckCircle />} color="success.main" onClick={() => navigate('/requests')} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
          <StatCard title={t('operations.totalServiceProviders')} value={stats.totalServiceProviders} icon={<Business />} color="secondary.main" onClick={() => navigate('/service-providers')} />
          <StatCard title={t('operations.activeServiceProviders')} value={stats.activeServiceProviders} icon={<CheckCircle />} color="success.main" onClick={() => navigate('/service-providers')} />
          <StatCard title={t('operations.insuranceManagement')} value={t('common.view')} icon={<HealthAndSafety />} color="primary.main" onClick={() => navigate('/insurance-companies')} />
        </Box>

        <Card elevation={2}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold">{t('operations.recentRequests')}</Typography>
            <IconButton size="small" onClick={() => navigate('/requests')}><ArrowForward /></IconButton>
          </Box>
          <CardContent>
            <List>
              {stats.recentRequests.length > 0 ? (
                stats.recentRequests.map((request: any, index: number) => (
                  <RecentRequestItem
                    key={request.id}
                    request={request}
                    index={index}
                    total={stats.recentRequests.length}
                    onNavigate={navigate}
                  />
                ))
              ) : (
                <ListItem><ListItemText primary={t('common.noData')} /></ListItem>
              )}
            </List>
          </CardContent>
        </Card>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mt: 4 }}>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/service-providers')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Business sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6">{t('operations.serviceProviders')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/service-providers/schedule')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Schedule sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
              <Typography variant="h6">{t('operations.serviceSchedule')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/insurance-companies')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <HealthAndSafety sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h6">{t('operations.insurance')}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
  );
};

export default OperationsOverview;
